import React, {useEffect, useState} from "react";
import {Link, useParams} from "react-router-dom";
import {apiUrl, useWindowDimensions} from "../App";
import remarkGfm from "remark-gfm";
import ReactMarkdown from "react-markdown";
import {ScrollToTopOnMount} from "./events";

// Convert single newlines to double newlines to preserve paragraph breaks in markdown
const preserveLineBreaks = (text: string): string => {
    if (!text) return "";
    // Replace single newlines with double newlines (paragraph breaks)
    // But don't affect already double newlines
    return text
        .replace(/\r\n/g, '\n')  // Normalize line endings
        .replace(/\n(?!\n)/g, '\n\n');  // Single newline -> double newline
};

interface IStoreItem {
    title: string,
    category: string,
    pages: number,
    isbn: string,
    slug: string,
    description: string,
    author: string,
    editor: string,
    price: number,
    published: boolean,
    image: string,
}

/* One individual item on the grid */
const StoreGridItem = (props: any) => {
    const {item} = props
    const url = `/bokhandel/${item?.slug}`
    
    // Format price - don't add ":-" suffix if price contains text like "slutsåld"
    const formatPrice = (price: string | number) => {
        if (!price) return "";
        const priceStr = String(price);
        // If price is numeric, add ":-" suffix, otherwise show as-is
        if (/^\d+$/.test(priceStr.trim())) {
            return `${priceStr} :-`;
        }
        return priceStr;
    };
    
    return (
        <article className="shop-item-column">
            <div className="shop-item-column-image">
                <Link to={url}>
                    <img src={item?.image} style={{width: "100%"}}/>
                </Link>
            </div>
            <div className="shop-item-column-content">
                <h1>
                    <Link to={url}>{item.title}</Link>
                </h1>
                <h2>&nbsp;</h2>
                <h2>{item?.category}</h2>
                {item?.pages ? <h2>{item.pages} sidor</h2> : null}
                {item?.isbn ? <h2>ISBN: {item.isbn}</h2> : null}
                <h2>&nbsp;</h2>
                <h2>{formatPrice(item?.price)}</h2>
            </div>

        </article>
    )
}

/**
 * View that shows a grid of available store items
 * @param props
 * @constructor
 */
export const StoreItemElement = (props: any) => {
    const {item} = props
    return (
        item ? <StoreGridItem item={item}/> : null
    )
}

export const StoreItemDetail = (props: any) => {
    const {slug} = useParams()
    const [item, setItem] = useState<IStoreItem | null>(null)

    const {width} = useWindowDimensions()
    const widthThreshold = 1024
    const isMobile = width <= widthThreshold

    useEffect(() => {
        const baseUrl = apiUrl()
        fetch(`${baseUrl}/api/store/${slug}/`)
            .then(response => response.json())
            .then(setItem)
            .catch(error => console.warn(error))
    }, [slug])

    // Format price - don't add ":-" suffix if price contains text like "slutsåld"
    const formatPrice = (price: string | number) => {
        if (!price) return "";
        const priceStr = String(price);
        // If price is numeric, add ":-" suffix, otherwise show as-is
        if (/^\d+$/.test(priceStr.trim())) {
            return `${priceStr} :-`;
        }
        return priceStr;
    };

    return (
        item ?
            <>
                <ScrollToTopOnMount/>
                <div className="breadcrumbs">
                    <Link to={"/bokhandel"}>Bokhandel &gt; {item?.title}</Link>
                </div>

                <main>
                    <section className="upcoming-events">
                        <article>
                            <div className={`text-content article-center-content wide-margin`}>
                                <img src={item.image} className="shop-item-image" alt={item.title}/>

                                <br/>
                                <h1 className={`${isMobile ? "headline-overflow" : ""}`}><Link
                                    to={`/bokhandel/${item?.slug}`}>{item?.title}</Link></h1>
                                <h2>{item.category}</h2>
                                {item.pages ? <h2>{item.pages} sidor</h2> : null}
                                {item.isbn ? <h2>ISBN: {item.isbn}</h2> : null}
                                <h2>{formatPrice(item.price)}</h2>

                                <br/>
                                <br/>

                                {
                                    item?.description
                                        ? <ReactMarkdown children={preserveLineBreaks(item.description)} remarkPlugins={[remarkGfm]}/>
                                        : null
                                }
                            </div>
                        </article>
                    </section>
                </main>
            </>
            : null
    )
}

/**
 * The main store page
 * @param props
 * @constructor
 */
export const Store = (props: any) => {

    const [items, setItems] = useState<IStoreItem[] | null>(null)

    useEffect(() => {
        const baseUrl = apiUrl()
        fetch(`${baseUrl}/api/store/`)
            .then(response => response.json())
            .then(data => Array.isArray(data) ? data : (data.results || []))
            .then(setItems)
    }, [])

    return (
        <main>
            <section className="">
                <div className="article-center-content store-grid-or-block">
                    {
                        items?.map((item: IStoreItem) => {
                                return (<StoreItemElement key={item.isbn} item={item}/>)
                            }
                        )
                    }
                </div>
            </section>
        </main>
    )
}
